import { useRef, useState, useEffect, forwardRef, useImperativeHandle, useCallback } from "react";

export default forwardRef(function DrawingCanvas({
    width = 1920,
    height = 1080,
    initialData,
    bgColor = '#ffffff',
    brushColor: brushColorProp,
    brushSize: brushSizeProp,
    mode: modeProp,
    onStrokeEnd,
}, ref) {
    const displayCanvasRef = useRef(null)      // visible canvas (bg + strokes composited)
    const strokesCanvasRef = useRef(null)       // off-screen canvas (strokes only, transparent bg)
    const cursorRef = useRef(null)              // cursor overlay element
    const lastPosRef = useRef(null)             // last drawing position for segment-based drawing
    const [drawing, setDrawing] = useState(false)
    const [hasContent, setHasContent] = useState(false)
    const [colorInternal, setColorInternal] = useState('#6366f1')
    const [sizeInternal, setSizeInternal] = useState(3)
    const [modeInternal, setModeInternal] = useState('draw')
    const [fillVertical, setFillVertical] = useState(false)
    const [cursorVisible, setCursorVisible] = useState(false)
    const initialDataLoadedRef = useRef(false)
    const bgColorRef = useRef(bgColor)
    bgColorRef.current = bgColor

    const color = brushColorProp ?? colorInternal
    const size = brushSizeProp ?? sizeInternal
    const mode = modeProp ?? modeInternal

    // Convert screen-space offset to local CSS coordinate space
    // (accounts for parent CSS transforms like storyboard zoom)
    const screenToLocal = useCallback((screenX, screenY) => {
        const canvas = displayCanvasRef.current
        if (!canvas) return { x: screenX, y: screenY }
        const rect = canvas.getBoundingClientRect()
        // offsetWidth/Height = CSS layout size (before transforms)
        // rect.width/height = rendered size (after transforms)
        const scaleX = canvas.offsetWidth / rect.width
        const scaleY = canvas.offsetHeight / rect.height
        return { x: screenX * scaleX, y: screenY * scaleY }
    }, [])

    // Compute the CSS pixel size of the brush cursor (in local coords)
    const getCursorSize = useCallback(() => {
        const canvas = displayCanvasRef.current
        if (!canvas) return size
        // Convert canvas-internal brush size → local CSS pixels
        return size * (canvas.offsetWidth / canvas.width)
    }, [size])

    // Update cursor position on mouse move over the canvas
    const updateCursor = useCallback((e) => {
        const cursor = cursorRef.current
        const canvas = displayCanvasRef.current
        if (!cursor || !canvas) return
        const rect = canvas.getBoundingClientRect()
        const screenX = e.clientX - rect.left
        const screenY = e.clientY - rect.top
        const local = screenToLocal(screenX, screenY)
        const csSize = getCursorSize()
        cursor.style.left = `${local.x}px`
        cursor.style.top = `${local.y}px`
        cursor.style.width = `${csSize}px`
        cursor.style.height = `${csSize}px`
    }, [getCursorSize, screenToLocal])

    // Composite: clear display → fill bgColor → draw strokes on top
    const composite = useCallback(() => {
        const display = displayCanvasRef.current
        const strokes = strokesCanvasRef.current
        if (!display || !strokes) return
        const ctx = display.getContext("2d")
        ctx.clearRect(0, 0, width, height)
        // Fill background
        const bg = bgColorRef.current
        if (bg && bg !== 'transparent') {
            ctx.fillStyle = bg
            ctx.fillRect(0, 0, width, height)
        }
        // Overlay strokes
        ctx.drawImage(strokes, 0, 0)
    }, [width, height])

    // Create off-screen strokes canvas on mount
    useEffect(() => {
        const offscreen = document.createElement('canvas')
        offscreen.width = width
        offscreen.height = height
        strokesCanvasRef.current = offscreen
        composite()
    }, [width, height, composite])

    // Load initial data onto the strokes canvas
    useEffect(() => {
        if (!initialData || initialDataLoadedRef.current) return
        const strokes = strokesCanvasRef.current
        if (!strokes) return
        const ctx = strokes.getContext("2d")
        const img = new Image()
        img.crossOrigin = 'anonymous'
        img.onload = () => {
            ctx.clearRect(0, 0, width, height)
            ctx.drawImage(img, 0, 0, width, height)
            setHasContent(true)
            initialDataLoadedRef.current = true
            composite()
        }
        img.src = initialData
    }, [initialData, width, height, composite])

    // Recomposite whenever bgColor changes
    useEffect(() => {
        composite()
    }, [bgColor, composite])

    const getPos = (e) => {
        const canvas = displayCanvasRef.current
        const rect = canvas.getBoundingClientRect()
        const clientX = e.touches?.[0]?.clientX ?? e.clientX
        const clientY = e.touches?.[0]?.clientY ?? e.clientY
        const scaleX = canvas.width / rect.width
        const scaleY = canvas.height / rect.height
        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY,
        }
    }

    const down = (e) => {
        e.preventDefault()
        e.stopPropagation()
        setDrawing(true)
        const strokes = strokesCanvasRef.current
        if (!strokes) return
        const ctx = strokes.getContext("2d")
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        ctx.lineWidth = size
        if (mode === 'erase') {
            ctx.globalCompositeOperation = 'destination-out'
            ctx.strokeStyle = 'rgba(0,0,0,1)'
        } else {
            ctx.globalCompositeOperation = 'source-over'
            ctx.strokeStyle = color
        }
        const p = getPos(e)
        lastPosRef.current = p
        // Draw a dot at the start point
        ctx.beginPath()
        ctx.arc(p.x, p.y, size / 2, 0, Math.PI * 2)
        ctx.fill()
        composite()
        setHasContent(true)
        updateCursor(e)
    }

    const move = (e) => {
        updateCursor(e)
        if (!drawing) return
        e.preventDefault()
        e.stopPropagation()
        const strokes = strokesCanvasRef.current
        if (!strokes) return
        const ctx = strokes.getContext("2d")
        const p = getPos(e)
        const prev = lastPosRef.current
        if (!prev) return
        // Draw individual line segment (not cumulative path)
        ctx.beginPath()
        ctx.moveTo(prev.x, prev.y)
        ctx.lineTo(p.x, p.y)
        ctx.stroke()
        lastPosRef.current = p
        composite()
        setHasContent(true)
    }

    const up = () => {
        if (drawing) {
            setDrawing(false)
            lastPosRef.current = null
            const strokes = strokesCanvasRef.current
            if (strokes) {
                strokes.getContext("2d").globalCompositeOperation = 'source-over'
            }
            composite()
            onStrokeEnd?.()
        }
    }

    const clear = () => {
        const strokes = strokesCanvasRef.current
        if (strokes) {
            strokes.getContext("2d").clearRect(0, 0, width, height)
        }
        composite()
        setHasContent(false)
    }

    // Export strokes only (transparent bg) — bgColor is stored separately on the model
    const getImageBase64 = () => {
        const strokes = strokesCanvasRef.current
        if (!strokes) return null
        return strokes.toDataURL("image/png")
    }

    // Export the composited display (bg + strokes) for preview purposes
    const getDisplayBase64 = () => {
        const display = displayCanvasRef.current
        if (!display) return null
        return display.toDataURL("image/png")
    }

    const getDisplayBase64Jpeg = () => {
        const display = displayCanvasRef.current
        if (!display) return null
        return display.toDataURL("image/jpeg", 1)
    }

    useImperativeHandle(ref, () => ({
        clear,
        getImageBase64,
        getDisplayBase64,
        getDisplayBase64Jpeg,
        hasContent,
        setColor: setColorInternal,
        setSize: setSizeInternal,
        setMode: setModeInternal,
    }))

    return (
        <div
            className="flex flex-col items-center justify-center w-full h-full"
            ref={el => {
                if (!el) return;
                if (!el._resizeObserver) {
                    el._resizeObserver = new window.ResizeObserver(entries => {
                        for (let entry of entries) {
                            if (entry.contentRect.width / entry.contentRect.height <= width / height) {
                                setFillVertical(false)
                            } else {
                                setFillVertical(true)
                            }
                        }
                    });
                    el._resizeObserver.observe(el);
                }
            }}
        >
            <div className={`relative ${fillVertical ? 'h-full' : 'w-full'}`} style={{ aspectRatio: `${width}/${height}` }}>
                <canvas
                    ref={displayCanvasRef}
                    width={width}
                    height={height}
                    className='w-full h-full touch-none'
                    style={{ cursor: 'none' }}
                    onMouseDown={down}
                    onMouseMove={move}
                    onMouseUp={up}
                    onMouseLeave={() => { up(); setCursorVisible(false) }}
                    onMouseEnter={() => setCursorVisible(true)}
                    onTouchStart={down}
                    onTouchMove={move}
                    onTouchEnd={up}
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                />
                {/* Brush cursor overlay */}
                <div
                    ref={cursorRef}
                    className='pointer-events-none absolute'
                    style={{
                        display: cursorVisible ? 'block' : 'none',
                        width: 0,
                        height: 0,
                        border: mode === 'erase'
                            ? '1.5px solid rgba(255,255,255,0.8)'
                            : '1.5px solid rgba(0,0,0,0.5)',
                        borderRadius: '50%',
                        transform: 'translate(-50%, -50%)',
                        boxShadow: mode === 'erase'
                            ? '0 0 0 1px rgba(0,0,0,0.3)'
                            : '0 0 0 1px rgba(255,255,255,0.3)',
                    }}
                />
            </div>
        </div>
    );
})